import {parseCookies} from 'nookies';
import {FOLLOW_COMPANY_RANKING_TERMS} from '../../../core/entities/company/constants';
import {
  useGetFollowAdCompanies,
  useGetFollowCompanyRankingList,
} from '../../../core/repositories/companyRepository';
import {
  useGetRecruitmentRankings,
  useRecruitmentList,
} from '../../../core/repositories/recruitingInfoRepository';
import {
  useGetQuickEntryRecruitmentList,
  useRecommendRecruitmentList,
} from '../../../core/repositories/recruitmentRepository';
import {useUsersMe} from '../../../core/repositories/usersMeRepository';
import {validateCookie} from '../../../core/usecases/auth/checkOauthTokenExistence';
import {CAROUSEL_CONFIG} from '../components/Container/constants';

const BASE_QUERY_PARAMS = {
  groupedJobTypeIDs: [] as number[],
  typeIDs: [] as number[],
  industryCategoryIDs: [] as number[],
  tagIDs: [] as number[],
  allgy: 'false' as const,
  page: 1,
  limit: CAROUSEL_CONFIG.RECRUITMENT_LIMIT,
};

type UseTopPageDataParams = {
  isLogin?: boolean;
};

export const useTopPageData = ({isLogin = true}: UseTopPageDataParams = {}) => {
  const cookies = parseCookies();
  const {isValid} = validateCookie(cookies);
  const {data: usersMe} = useUsersMe({
    config: {enabled: isValid},
  });
  // ログイン済みかつエンジニア志望ではない場合はエンジニア職の募集情報を取得しない
  // usersMe未確定時はundefinedにしてクエリパラメータに含めない
  const excludeEngineer =
    usersMe?.ok && !usersMe.value.isEngineer ? true : undefined;

  const recruitmentRankingList = useGetRecruitmentRankings({
    queryParams: {
      limit: CAROUSEL_CONFIG.RECRUITMENT_RANKING_LIMIT,
    },
  });

  // おすすめはログイン状態の時のみ取得
  const recommendRecruitmentList = useRecommendRecruitmentList({
    limit: CAROUSEL_CONFIG.RECRUITMENT_LIMIT,
    config: {
      enabled: isLogin,
    },
  });

  const quickEntryRecruitmentList = useGetQuickEntryRecruitmentList({
    limit: CAROUSEL_CONFIG.RECRUITMENT_LIMIT,
    isShuffle: true,
    excludeEngineer: excludeEngineer,
  });

  const deadlineRecruitmentList = useRecruitmentList({
    queryParams: {
      ...BASE_QUERY_PARAMS,
      excludeEngineer: excludeEngineer,
      order: 'deadline',
    },
  });

  const recentRecruitmentList = useRecruitmentList({
    queryParams: {
      ...BASE_QUERY_PARAMS,
      excludeEngineer: excludeEngineer,
      order: 'recent',
    },
  });

  const gssktEventRecruitmentList = useRecruitmentList({
    queryParams: {
      ...BASE_QUERY_PARAMS,
      typeIDs: [7], // 外資就活主催
      order: 'recent',
    },
  });

  const adCompanies = useGetFollowAdCompanies({
    queryParams: {
      limit: CAROUSEL_CONFIG.COMPANY_LIMIT,
      contractOnly: true,
    },
  });

  const followRankingList = useGetFollowCompanyRankingList({
    limit: CAROUSEL_CONFIG.COMPANY_LIMIT,
    term: FOLLOW_COMPANY_RANKING_TERMS.daily,
  });

  // ランキングデータからRecruitment[]を抽出
  const rankingRecruitments = recruitmentRankingList.data?.map(
    (item) => item.recruitment,
  );

  // レコメンドリクルートメントデータからRecruitment[]を抽出
  const recommendRecruitments = recommendRecruitmentList.data?.ok
    ? recommendRecruitmentList.data.value.recruitments
    : undefined;

  // 質問なしエントリー募集は全ページのRecruitment[]を結合して抽出
  const quickEntryRecruitments = quickEntryRecruitmentList.data?.pages.flatMap(
    (page) => (page.ok ? page.value.recruitments : []),
  );

  return {
    recruitments: {
      ranking: rankingRecruitments,
      deadline: deadlineRecruitmentList,
      recommend: recommendRecruitments,
      recent: recentRecruitmentList,
      gssktEvent: gssktEventRecruitmentList,
      quickEntry: quickEntryRecruitments,
    },
    quickEntryPagination: {
      fetchNextPage: quickEntryRecruitmentList.fetchNextPage,
      hasNextPage: quickEntryRecruitmentList.hasNextPage ?? false,
      isFetchingNextPage: quickEntryRecruitmentList.isFetchingNextPage,
    },
    companies: {
      ad: adCompanies,
      followRanking: followRankingList,
    },
    isLoading: {
      recruitmentRanking: recruitmentRankingList.isLoading,
      recruitments: isLogin ? recommendRecruitmentList.isLoading : false,
      deadline: deadlineRecruitmentList.isLoading,
      recent: recentRecruitmentList.isLoading,
      gssktEvent: gssktEventRecruitmentList.isLoading,
      quickEntry: quickEntryRecruitmentList.isLoading,
      companies: adCompanies.isLoading,
    },
  };
};
